import { CloseOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";

export default function FindReplaceModal({ grid, onClose }) {
  const [findTerm, setFindTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeCell, setWholeCell] = useState(false);
  const [replaceCount, setReplaceCount] = useState(0);
  const [foundCells, setFoundCells] = useState([]);
  const [currentFoundIndex, setCurrentFoundIndex] = useState(-1);

  // Find all occurrences of the term
  const handleFind = () => {
    if (!findTerm || !grid) return;
    try {
      const matches = [];
      const data = grid.data;

      // Validate data
      if (!Array.isArray(data)) {
        return 0;
      }

      // Get valid column names (exclude internal properties and functions)
      const getValidColumnNames = (row) => {
        return Object.keys(row).filter(
          (key) =>
            typeof row[key] !== "function" &&
            !key.startsWith("__") &&
            key !== "length"
        );
      };

      // Process each row safely
      data.forEach((row, rowIndex) => {
        // Skip if row is undefined or not an object
        if (!row || typeof row !== "object") return;

        const columnNames = getValidColumnNames(row);

        columnNames.forEach((key) => {
          // Convert cell value to string safely
          const cellValue = row[key] != null ? String(row[key]) : "";
          const searchTerm = caseSensitive ? findTerm : findTerm.toLowerCase();
          const searchValue = caseSensitive
            ? cellValue
            : cellValue.toLowerCase();

          // Match according to search settings
          if (wholeCell) {
            if (searchValue === searchTerm) {
              matches.push({ rowIndex, columnName: key });
            }
          } else {
            if (searchValue.includes(searchTerm)) {
              matches.push({ rowIndex, columnName: key });
            }
          }
        });
      });

      setFoundCells(matches);
      setCurrentFoundIndex(matches.length > 0 ? 0 : -1);

      // Select and scroll to the first match if found
      if (matches.length > 0) {
        selectAndScrollToCell(matches[0]);
      }

      return matches.length;
    } catch (err) {
      console.error("Find operation error:", err);
      return 0;
    }
  };

  // Select and scroll to a specific cell
  const selectAndScrollToCell = (cellLocation) => {
    if (!grid || !cellLocation) return;

    try {
      // Check if the row exists in the grid data
      if (cellLocation.rowIndex >= grid.data.length) {
        return;
      }

      const row = grid.data[cellLocation.rowIndex];
      // Check if the column exists in the row
      if (!row.hasOwnProperty(cellLocation.columnName)) {
        return;
      }

      const colIndex = Object.keys(grid.data[0]).indexOf(
        cellLocation.columnName
      );
      // Use appropriate grid methods with error handling
      try {
        // For canvas-datagrid, we need to ensure the view is updated before scrolling
        // grid.scrollIntoView(colIndex, cellLocation.rowIndex);
        grid.gotoCell(colIndex, cellLocation.rowIndex);
        grid.setActiveCell(colIndex, cellLocation.rowIndex);
      } catch (scrollError) {
        console.error("Error scrolling to cell:", scrollError);
      }
    } catch (err) {
      console.error("Cell selection error:", err);
    }
  };

  // Navigate to next match
  const findNext = () => {
    if (foundCells.length === 0) {
      handleFind();
      return;
    }

    const nextIndex = (currentFoundIndex + 1) % foundCells.length;
    setCurrentFoundIndex(nextIndex);
    selectAndScrollToCell(foundCells[nextIndex]);
  };

  // Navigate to previous match
  const findPrevious = () => {
    if (foundCells.length === 0) {
      handleFind();
      return;
    }

    const prevIndex =
      (currentFoundIndex - 1 + foundCells.length) % foundCells.length;
    setCurrentFoundIndex(prevIndex);
    selectAndScrollToCell(foundCells[prevIndex]);
  };

  // Replace the current match
  const replaceCurrent = () => {
    if (foundCells.length === 0 || currentFoundIndex === -1 || !grid) return;

    const { rowIndex, columnName } = foundCells[currentFoundIndex];
    const cellValue = String(grid.data[rowIndex][columnName] || "");

    let newValue;
    if (wholeCell) {
      newValue = replaceTerm;
    } else {
      // Replace only the matching part, preserving case if not case sensitive
      const searchTerm = caseSensitive ? findTerm : new RegExp(findTerm, "gi");
      newValue = cellValue.replace(searchTerm, replaceTerm);
    }

    // Update the cell value
    grid.data[rowIndex][columnName] = newValue;
    grid.draw();

    // Move to the next match
    // findNext();
    setReplaceCount((prev) => prev + 1);
  };

  // Replace all matches
  const replaceAll = () => {
    if (!findTerm || !grid) return;

    const data = [...grid.data];
    let count = 0;

    data.forEach((row, rowIndex) => {
      Object.keys(row).forEach((key) => {
        if (key === "__selectedRowHeaderValue") return;

        const cellValue = String(row[key] || "");
        const searchTerm = caseSensitive ? findTerm : findTerm.toLowerCase();
        const searchValue = caseSensitive ? cellValue : cellValue.toLowerCase();

        if (wholeCell) {
          if (searchValue === searchTerm) {
            grid.data[rowIndex][key] = replaceTerm;
            count++;
          }
        } else {
          if (searchValue.includes(searchTerm)) {
            // Replace while preserving case if not case sensitive
            const regex = caseSensitive
              ? new RegExp(findTerm, "g")
              : new RegExp(findTerm, "gi");
            grid.data[rowIndex][key] = cellValue.replace(regex, replaceTerm);
            count++;
          }
        }
      });
    });

    grid.draw();
    setReplaceCount(count);

    // Reset search after replace all
    setFoundCells([]);
    setCurrentFoundIndex(-1);

    return count;
  };

  // Close modal with Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.ctrlKey && e.key === "f") {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed z-50 w-full pb-5 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto animate-fadeIn md:w-auto shadow-3xl rounded-4 bg-secondary-main top-1/2 left-1/2">
      {/* header */}
      <div className="relative w-full py-3 pl-5">
        <label className="text-xl font-semibold text-[#107c41]">
          Find and Replace
        </label>
        <CloseOutlined
          onClick={() => {
            onClose();
            grid.draw();
          }}
          className="absolute top-0 right-0 !p-2 cursor-pointer hover:bg-gray-200"
        />
      </div>

      {/* content */}
      <div className="flex flex-col gap-2 px-5">
        {/* find input */}
        <div className="flex flex-col gap-1">
          <label className="text-[#323130] font-semibold text-sm">Find</label>
          <input
            placeholder="Text to find"
            className="border border-[#8a8886] px-1.5 h-9 focus:!outline-none"
            onChange={(e) => setFindTerm(e.target.value)}
            value={findTerm}
            autoFocus
          />
        </div>
        {/* replace input */}
        <div className="flex flex-col gap-1">
          <label className="text-[#323130] font-semibold text-sm">
            Replace with
          </label>
          <input
            placeholder="Insert text"
            className="border border-[#8a8886] px-1.5 h-9 focus:!outline-none"
            onChange={(e) => setReplaceTerm(e.target.value)}
            value={replaceTerm}
          />
        </div>
        {/* options */}
        <div className="flex items-center">
          <input
            id="caseSensitive"
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
            className="w-4 h-4 border-gray-300 rounded accent-primary-tint"
          />
          <label htmlFor="caseSensitive" className="ml-2 text-sm text-gray-700">
            Match case
          </label>
        </div>

        <div className="flex items-center">
          <input
            id="wholeCell"
            type="checkbox"
            checked={wholeCell}
            onChange={(e) => setWholeCell(e.target.checked)}
            className="w-4 h-4 border-gray-300 rounded accent-primary-tint"
          />
          <label htmlFor="wholeCell" className="ml-2 text-sm text-gray-700">
            Match entire cell content
          </label>
        </div>
      </div>

      {/* Status message */}
      <div className="grid grid-cols-1 px-5 mt-2 md:grid-cols-2">
        {foundCells.length > 0 && (
          <div className="text-xs text-gray-600 md:text-sm">
            {currentFoundIndex + 1} of {foundCells.length} matches
          </div>
        )}

        {replaceCount > 0 && (
          <div className="text-xs text-green-600 md:text-sm">
            Replaced {replaceCount} occurrence(s)
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center justify-center w-full gap-2 px-5 mt-6 md:flex-nowrap md:justify-end">
        <button
          onClick={handleFind}
          disabled={!findTerm}
          className="h-8 hover:brightness-125 px-4 bg-[#107c41] rounded-4 text-white font-semibold text-sm"
        >
          Find
        </button>
        <button
          onClick={findNext}
          disabled={foundCells.length === 0}
          className="h-8 hover:brightness-125 px-4 bg-[#107c41] rounded-4 text-white font-semibold text-sm"
        >
          Next
        </button>
        <button
          onClick={findPrevious}
          disabled={foundCells.length === 0}
          className="h-8 hover:brightness-125 px-4 bg-[#107c41] rounded-4 text-white font-semibold text-sm"
        >
          Previous
        </button>
        <button
          onClick={replaceCurrent}
          disabled={foundCells.length === 0 || currentFoundIndex === -1}
          className="h-8 hover:bg-gray-200 px-4 text-sm font-semibold border border-[#8a8886] rounded-4"
        >
          Replace
        </button>

        <button
          onClick={replaceAll}
          disabled={!findTerm}
          className="h-8 hover:bg-gray-200 px-4 text-sm font-semibold border border-[#8a8886] rounded-4"
        >
          Replace All
        </button>
      </div>
    </div>
  );
}
